import { Autocomplete, Box, Chip, TextField } from "@mui/material"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker"
import { StyledButton } from "components/Button/StyledButton"
import { ResponsiveDialog } from "components/ResponsiveDialog/ResponsiveDialog"
import dayjs from "dayjs"
import { Reminder, User } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { enqueueSnackbar } from "notistack"
import { useEffect, useState } from "react"
import { emptyPerson } from "store/store"
import { useLocalStorage } from "utils/useLocalStorage"

type SetDateTimeDialogProps = {
  open: boolean
  onClose: () => void
}

export const SetDateTimeDialog = (props: SetDateTimeDialogProps) => {
  const { open, onClose } = props
  const users = useStoreState((state) => state.users)
  const [currentUser] = useLocalStorage<User>("user", emptyPerson)
  const [reminder, setReminder] = useState<Reminder>({
    time: [],
    person: [],
    note: "",
    title: "",
    location: "",
    creator: currentUser,
  })

  const { fetchUsers } = useStoreActions((actions) => actions)

  useEffect(() => {
    const loadUsers = async () => {
      try {
        await fetchUsers({ fresh: false })
      } catch (err) {
        console.error("Error loading users:", err)
      }
    }
    loadUsers()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setReminder({
      time: [],
      person: [],
      note: "",
      title: "",
      location: "",
      creator: currentUser,
    })
  }, [currentUser, open])

  const handleClose = () => {
    onClose()
  }

  const handleSave = () => {
    if (reminder) {
      enqueueSnackbar("Noch nicht weiter implementiert", { variant: "info" })
      handleClose()
    }
  }

  return (
    <ResponsiveDialog open={open} onClose={onClose}>
      <DialogTitle>Erinnerung hinzufügen</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Autocomplete
          size="small"
          multiple
          id="tags-outlined"
          options={users}
          getOptionLabel={(option) => option?.displayName ?? ""}
          filterSelectedOptions
          value={reminder.person}
          renderInput={(params) => (
            <TextField
              sx={{ mt: 1 }}
              {...params}
              label="Wer soll erinnert werden?"
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.displayName}
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={index}
                label={`${option.displayName}`}
                size="small"
              />
            ))
          }
          onChange={(_, value) => {
            if (value) {
              setReminder({ ...reminder, person: value })
            }
          }}
        />
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {reminder.time.map((time, index) => (
            <Chip
              key={index}
              label={dayjs(time).format("DD.MM.YYYY HH:mm")}
              onDelete={() => {
                const newTimes = reminder.time.filter((_, i) => i !== index)
                setReminder({ ...reminder, time: newTimes })
              }}
            />
          ))}
        </Box>
        <DateTimePicker
          label="Wann"
          onAccept={(newValue, ctx) => {
            if (newValue) {
              setReminder({
                ...reminder,
                time: [...reminder.time, newValue.toISOString()],
              })
            }
          }}
          displayWeekNumber
          disablePast
        />
        <TextField
          fullWidth
          multiline
          label="Titel"
          value={reminder.title}
          onChange={(event) => {
            if (event)
              setReminder({
                ...reminder,
                title: event.currentTarget.value,
              })
          }}
        />

        <TextField
          fullWidth
          multiline
          label="Kommentar"
          value={reminder.note}
          onChange={(event) => {
            if (event)
              setReminder({
                ...reminder,
                note: event.currentTarget.value,
              })
          }}
        />
        <TextField
          fullWidth
          multiline
          label="Ort"
          value={reminder.location}
          onChange={(event) => {
            if (event)
              setReminder({
                ...reminder,
                location: event.currentTarget.value,
              })
          }}
        />
      </DialogContent>
      <DialogActions>
        <StyledButton buttonType="cancel" onClick={handleClose} fullWidth>
          Abbrechen
        </StyledButton>
        <StyledButton buttonType="save" onClick={handleSave} fullWidth>
          Speichern
        </StyledButton>
      </DialogActions>
    </ResponsiveDialog>
  )
}
